import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { MRI_KEY_BY_RISK_LEVEL } from '../../../types';

interface BuildingLoadRatingBarProps {
  windLoadMph: number;
  snowLoadPsf: number;
  isPermanentDwelling: boolean;
  isLoading?: boolean;
  hasError?: boolean;
}

const BuildingLoadRatingBar: React.FC<BuildingLoadRatingBarProps> = ({
  windLoadMph,
  snowLoadPsf,
  isPermanentDwelling,
  isLoading = false,
  hasError = false,
}) => {
  const riskLevel = isPermanentDwelling ? 2 : 1;
  const mriKey = MRI_KEY_BY_RISK_LEVEL[riskLevel];
  const riskLabel = isPermanentDwelling ? 'II (Permanent Dwelling)' : 'I (Standard)';

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2.5,
        flexWrap: 'wrap',
      }}
    >
      <Typography variant="body2" fontWeight={600} color="primary.dark">
        Building Load Ratings
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Wind Load:
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {isLoading ? (
            <CircularProgress size={12} />
          ) : hasError ? (
            <Typography component="span" color="error" variant="body2" fontWeight={600}>
              Error
            </Typography>
          ) : (
            `${windLoadMph} mph`
          )}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Snow Load:
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {isLoading ? (
            <CircularProgress size={12} />
          ) : hasError ? (
            <Typography component="span" color="error" variant="body2" fontWeight={600}>
              Error
            </Typography>
          ) : (
            `${snowLoadPsf} psf`
          )}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Risk Category:
        </Typography>
        <Typography
          variant="body2"
          fontWeight={600}
          color={isPermanentDwelling ? 'warning.dark' : 'success.dark'}
        >
          {riskLabel}
        </Typography>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
        ASCE-derived &bull; {mriKey}-yr MRI
      </Typography>
    </Box>
  );
};

export default BuildingLoadRatingBar;
